import { Fragment, lazy, useState } from 'react';
import classNames from 'classnames';
import { IconDoctorExperience, IconDoctorLanguages, IconGraduation, IconInsuranceShield, IconLocationPin } from '@medifind/icons';
import { IconPhoneFill } from '@medifind/icons';
import { toSlug } from '@medifind/router';
import {
  Card,
  CardActionButton,
  CardAddress,
  CardAffiliation,
  CardConditionTier,
  CardLabelledValue,
  CardOrgLogo,
  CardRatings,
  CardValue,
  ClaimedProfileBadge,
} from '@medifind/search-results';
import { Headshot, MFLazyLoad } from '@medifind/shared-basic-components';
import { MFLink } from '@medifind/shared-basic-components';
import { CardConditionBadgeContainer, Skeleton } from '@medifind/shared-basic-components';
import { getSmallClassList, getValueMediaBreakpointDown, stripAllHtml } from '@medifind/utils';
import { useMediaQueryDown } from '@medifind/utils';
import { logEventGA4 } from '@medifind/zustand';
import styles from './DoctorCard.module.scss';
const YoutubeVideo = lazy(() => import('@medifind/lazy-loadable/lib/shared-basic-components/LazyYoutubeVideo'));
const formatBiography = (biography = '') => {
  const bioText = biography.replace(/<\/?p>/g, '');
  return bioText;
};

const getLink = ({ absoluteUrl, codingSlug, projectId, doctor, codingType, personId, showInsurance, additionalQueryParams }) => {
  if (!personId) return null;
  const queryParamsArr = [...(additionalQueryParams ? [additionalQueryParams] : [])];
  const queryParams = queryParamsArr.length > 0 ? `?${queryParamsArr.join('&')}` : '';
  const personSlug = doctor ? toSlug(doctor.name) : null;
  const url = absoluteUrl ? process.env.NX_LOCAL_HOST : '';
  if (projectId && codingSlug) {
    return `${url}/${
      codingType === 'procedure' ? 'procedures' : 'conditions'
    }/${codingSlug}/${projectId}/doctors/${personSlug}/${personId}${queryParams}`;
  }
  return `${url}/doctors/${personSlug}/${personId}${queryParams}`;
};

const topCodingsSpecifications = {
  averageLength: 50,
  minCount: 1,
  maxCount: 4,
};
const TopCodings = ({ topCodings, codingCount, profileLink, isUSBased }) => {
  const { minCount, maxCount, averageLength } = topCodingsSpecifications;
  if (!topCodings?.length) return null;
  let currentLen = 0;
  let pickedI = 0;
  // Identify how many conditions should be shown based on the requested average length of the string
  topCodings
    .map(({ codingName }) => codingName.length)
    .some((len, i, arr) => {
      // If were passed the min count
      if (i >= minCount && i > 0) {
        // If this and the previous length is between the average length
        if (currentLen - arr[i - 1] <= averageLength && averageLength <= currentLen) {
          // Pick either the previous or the current value based on what is closer in length
          if (currentLen - averageLength > averageLength - (currentLen - arr[i - 1])) {
            pickedI = i - 1;
            return true;
          } else {
            pickedI = i;
            return true;
          }
        }
      }

      // If end of list OR
      // If the next one goes over the max, pick this one
      if (i === arr.length - 1 || i == maxCount + 1) {
        pickedI = i;
        return true;
      }

      currentLen += len;
    });
  return (
    <div className={styles['top-codings']}>
      <div className={styles['top-codings__title']}>Top Areas of Expertise</div>
      <div>
        {topCodings.slice(0, pickedI).map(({ type, projectId, codingName }, i, arr) => {
          return (
            <Fragment key={i}>
              <MFLink to={`/${type}s/${toSlug(codingName)}/${projectId}`}>{codingName}</MFLink>
              {i < arr.length - 1 || codingCount > i + 1 ? <span className={styles['top-codings__divider']}> | </span> : null}
              {i === arr.length - 1 && codingCount > i + 1 ? (
                <MFLink rel={!isUSBased ? 'nofollow' : null} to={profileLink}>
                  See More
                </MFLink>
              ) : null}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};
const PhoneNumberButton = ({ personId, hidePartialPhoneNumber, phone, color, iconColor, smallXPadding, xText, smallClassList }) => {
  const [allowPhoneNumber, setAllowPhoneNumber] = useState(!hidePartialPhoneNumber);
  const isMobile = useMediaQueryDown('xs');

  if (!phone) return null;
  return (
    <CardActionButton
      onClick={() => {
        !isMobile && !allowPhoneNumber && logEventGA4('call');
        setAllowPhoneNumber(true);
      }}
      disabled={!personId}
      label={allowPhoneNumber || isMobile ? `Call ${phone}` : xText || `Show Phone Number`}
      color={color}
      to={allowPhoneNumber || isMobile ? `tel:${phone}` : ``}
      analyticsLinkType={'person-call'}
      noWrapping={false}
      smallXPadding={smallXPadding}
    />
  );
};

const Pill = ({ text }) => {
  return <div className={styles['badge-pill']}>{text}</div>;
};

const DoctorCard = (props) => {
  const {
    doctor,
    codingSlug,
    codingType,
    codingName,
    projectId,
    style,
    onClick,
    showConditionCount = true,
    showPhone = true,
    showGradInstitution = false,
    showInsurance = true,
    showBiography = true,
    showLanguages = true,
    showExperience = true,
    alternateLocationDisplay = true,
    showYoutube = true,
    absoluteUrl,
    smallAt = ['xs', 'sm', 'md', 'lg'],
    useFeaturedCard,
    hideSponsorTag = false,
    classes = {},
    priority,
    showMiddleInitial = true,
    additionalQueryParams,
    iconColor = 'blue',
    viewProfileButtonColor = 'blue',
    wideButton = false,
    appointmentAssistOptInButtonColor = 'red',
  } = props;
  const {
    personId,
    distance,
    biography,
    primaryOrgName,
    primaryOrgWebsite,
    credentials,
    phone,
    appointmentPhone,
    appointmentURL,
    appointmentAssistOptIn,
    highlyRatedConditionsCount,
    isUSPrescriber,
    languages,
    orgLogo,
    orgWebsite,
    youtubeVideos,
    isPromoted,
    isFeatured,
    topCodings,
    codingCount,
    badges,
    displayName,
    displayNameShort,
    address,
    score,
    name,
    specialties,
    insurance,
    distanceValue,
    distanceUnit,
    customBadges,
    yearsOfExperience,
  } = doctor || {};
  const isFeaturedCard = isFeatured || useFeaturedCard;
  const HeaderTag = priority ? `h${priority}` : 'div';
  const isUSBased = address?.country2Code === 'US';
  const { gradInstitution } = credentials || {};
  const link = getLink({
    absoluteUrl,
    codingSlug,
    projectId,
    doctor,
    codingType,
    personId,
    showInsurance,
    additionalQueryParams,
  });
  const onlineBookingLink = !link ? null : link.includes('?') ? `${link}&bookOnline=true` : `${link}?bookOnline=true`;

  const phoneNumber = appointmentPhone ?? (phone ? phone : address?.orgPhoneNumber ? address.orgPhoneNumber : null);
  const { smallClassList } = getSmallClassList(smallAt, styles);
  const showScore = score <= 5 || score == null ? false : props.showScore;
  const showViewProfileBtn = !Boolean(appointmentAssistOptIn);

  const doctorLanguages = languages?.length > 0 && isUSPrescriber ? languages.join(', ') : null;

  const sideContainerItems = [
    showScore && <CardConditionTier key={'CardConditionTier'} doctor={doctor} conditionName={codingName} smallAt={smallAt} />,
    showConditionCount && (
      <CardRatings key={'CardRatings'} data={doctor} smallAt={smallAt} highlyRatedConditionsCount={highlyRatedConditionsCount} />
    ),
  ];

  return (
    <Card
      id={`card_doctor${isFeaturedCard ? '-feat' : ''}_${personId}`} // Id is used by GA, don't change
      style={style}
      smallAt={smallAt}
      hasData={!!doctor}
      cardTop={!hideSponsorTag && isFeaturedCard && <div className={styles['featured-tag']}>Featured</div>}
      icon={<Headshot doctor={doctor} showScore={showScore} useV2Style />}
      onClick={onClick}
      link={{
        to: link,
        target: absoluteUrl ? '_blank' : null,
        disabled: !personId,
        type: 'icon',
        'aria-label': (showMiddleInitial ? displayName : displayNameShort) || name,
        analyticsLinkType: 'doctor-name',
      }}
      rel={!isUSBased ? 'nofollow' : null}
      titleHeader={
        alternateLocationDisplay &&
        distance && (
          <div className={classNames(styles['header__distance'], classes.headerDistance)}>
            {distanceValue} {distanceUnit === 'mi' ? 'Miles' : 'Kilometers'} Away
          </div>
        )
      }
      title={
        <>
          <div className={classNames(styles['header__name'], classes.headerSpecialties)}>
            <MFLink
              to={link}
              onClick={onClick}
              target={absoluteUrl ? '_blank' : null}
              disabled={!personId}
              className={styles['header__link']}
              noTabIndex={showViewProfileBtn}
              rel={!isUSBased ? 'nofollow' : null}
              analyticsLinkType={'doctor-name'}
            >
              <HeaderTag className={styles['header__name--text']}>{(showMiddleInitial ? displayName : displayNameShort) || name}</HeaderTag>
            </MFLink>{' '}
            <span className={styles['header__name--icon-tick']}>
              <ClaimedProfileBadge doctor={doctor} position="bottom-centered" />
            </span>
          </div>
          <div className={classNames(styles['header__specialties'], classes.headerSpecialties)}>
            {doctor ? (specialties || []).join(' | ') : <Skeleton />}
          </div>
        </>
      }
      classes={{
        metadataContainer: classNames({ [styles['condition-count']]: showConditionCount }),
        header: classNames(styles['doctor-card__header'], smallClassList),
        card: classes.bodyWrapper,
        actionButtonWrapper: classes.actionButtonWrapper,
      }}
      metadata={{
        first: (
          <>
            {!alternateLocationDisplay ? (
              <div className={classNames(styles['body__container'], smallClassList)}>
                <div>
                  <CardAffiliation
                    analyticsLinkType={'person-org-link'}
                    isDataLoaded={!!doctor}
                    link={primaryOrgWebsite}
                    display={primaryOrgName}
                    smallAt={smallAt}
                  />
                  <CardAddress data={doctor} distance={distance} />
                </div>
                {orgLogo && <CardOrgLogo orgWebsite={orgWebsite} orgLogo={orgLogo} primaryOrgName={primaryOrgName} />}
              </div>
            ) : (
              <div className={styles['body__alt-container']}>
                <div>{doctor ? <IconLocationPin size={16} color={iconColor} /> : <Skeleton width={12} />}</div>
                <div style={{ width: '100%' }}>
                  {doctor ? (
                    primaryOrgName ? (
                      <h4 className={styles['body__alt-container__affiliation']}>{primaryOrgName}</h4>
                    ) : null
                  ) : (
                    <Skeleton count={2} />
                  )}
                  <CardAddress data={doctor} distance={distance} />
                </div>
              </div>
            )}
            {doctor?.centerOfExcellence && <CardConditionBadgeContainer badgeList={doctor?.centerOfExcellence} />}
            {showPhone && (!doctor || phone) ? (
              <div className={styles['body__alt-container']}>
                <div>{doctor ? <IconPhoneFill color={iconColor} size={12} /> : <Skeleton width={12} />}</div>
                {doctor ? (
                  <CardValue
                    value={
                      <MFLink analyticsLinkType={'doctor-phone'} href={`tel:${phone}`}>
                        {phone}
                      </MFLink>
                    }
                  />
                ) : (
                  <Skeleton />
                )}
              </div>
            ) : null}
            {showExperience && yearsOfExperience > 0 && (
              <div className={styles['body__alt-container']}>
                <div>
                  <IconDoctorExperience size={12} color={iconColor} />
                </div>
                <CardLabelledValue value={`${yearsOfExperience}+ years`} label={'Experience:'} />
              </div>
            )}

            {showLanguages && !!doctorLanguages && (
              <div className={styles['body__alt-container']}>
                <div>{doctor ? <IconDoctorLanguages size={12} color={iconColor} /> : <Skeleton width={12} />}</div>
                <div style={{ width: '100%' }}>
                  <CardLabelledValue value={doctorLanguages} label={'Languages Spoken:'} />
                </div>
              </div>
            )}
            {showInsurance && insurance?.length > 0 && (
              <div className={styles['body__alt-container']}>
                <div>{doctor ? <IconInsuranceShield size={12} color={iconColor} /> : <Skeleton width={12} />}</div>
                <CardValue
                  value={
                    isUSPrescriber ? (
                      <MFLink
                        to={link + '#insurance'}
                        onClick={onClick}
                        target={absoluteUrl ? '_blank' : undefined}
                        analyticsLinkType={'insurances'}
                      >
                        See accepted insurances
                      </MFLink>
                    ) : null
                  }
                />
              </div>
            )}
            {showGradInstitution && gradInstitution && (
              <div className={styles['body__alt-container']}>
                <div>{doctor ? <IconGraduation size={12} color={iconColor} /> : <Skeleton width={12} />}</div>
                <CardLabelledValue
                  label={gradInstitution?.name ? 'Graduate Institution' : 'Graduation Year'}
                  value={
                    gradInstitution?.name || gradInstitution?.gradYearNumber ? (
                      <>{[gradInstitution.name, gradInstitution.gradYearNumber].filter(Boolean).join(' | ')}</>
                    ) : null
                  }
                />
              </div>
            )}

            {badges?.acceptsNewPatients || badges?.telemedicine ? (
              <div className={styles['badges-container']}>
                {badges?.acceptsNewPatients ? <Pill text="Accepting New Patients" /> : null}
                {badges?.telemedicine ? <Pill text="Offers Telehealth" /> : null}
              </div>
            ) : null}

            {customBadges?.length > 0 && (
              <div className={styles['custom-badge-container']}>
                {customBadges.map((d) => (
                  <div key={d.id} className={styles['custom-badge']}>
                    {d.badgeLogo && (
                      <div>
                        <CardOrgLogo orgWebsite={d.url} orgLogo={d.badgeLogo} primaryOrgName={d.name} />
                      </div>
                    )}
                    {d.text && <div dangerouslySetInnerHTML={{ __html: d.text }}></div>}
                  </div>
                ))}
              </div>
            )}
          </>
        ),
        keyValue: [],
        last: (
          <div className={wideButton ? '' : styles['last-container']}>
            {isFeaturedCard && <TopCodings topCodings={topCodings} codingCount={codingCount} profileLink={link} isUSBased={isUSBased} />}
            <div className={classNames(styles['bio-youtube-container'], smallClassList)}>
              {showBiography && (
                <div className={styles['body__biography']}>
                  {doctor ? (
                    <p>{formatBiography(stripAllHtml(biography))}</p>
                  ) : (
                    <Skeleton count={getValueMediaBreakpointDown({ xs: 6, sm: 5, default: 3 })} />
                  )}
                </div>
              )}
              {/* youtube videos */}
              {showYoutube && youtubeVideos?.length > 0 ? (
                <div className={classNames(styles['youtube'], { [styles['youtube--half-width']]: isFeaturedCard }, smallClassList)}>
                  <MFLazyLoad height={getValueMediaBreakpointDown({ xs: 200, default: 115 })}>
                    <YoutubeVideo title={youtubeVideos?.[0]?.title} youtubeId={youtubeVideos?.[0]?.videoId} />
                  </MFLazyLoad>
                </div>
              ) : null}
            </div>
          </div>
        ),
      }}
      sideContainer={sideContainerItems.filter(Boolean).length ? sideContainerItems : null}
      buttons={
        <div className={wideButton ? styles['last-container'] : ''}>
          {isPromoted && (
            <PhoneNumberButton
              color="inverted-blue"
              phone={phoneNumber}
              personId={personId}
              hidePartialPhoneNumber
              smallXPadding={true}
              smallClassList={smallClassList}
            />
          )}
          {appointmentAssistOptIn ? (
            <CardActionButton
              disabled={!personId}
              label={appointmentURL || absoluteUrl ? 'Schedule Now' : 'Request Appointment'}
              analyticsLinkType={appointmentURL || absoluteUrl ? 'schedule-appointment' : 'request-appointment'}
              color={appointmentAssistOptInButtonColor}
              absoluteUrl={appointmentURL || absoluteUrl}
              to={appointmentURL ?? onlineBookingLink}
              noWrapping={false}
              wideCard={wideButton}
              smallXPadding={true}
            />
          ) : null}
          {showViewProfileBtn ? (
            <CardActionButton
              disabled={!personId}
              label="View Profile"
              color={viewProfileButtonColor}
              absoluteUrl={absoluteUrl}
              to={link}
              wideCard={wideButton}
              onClick={onClick}
              noWrapping={false}
              rel={!isUSBased ? 'nofollow' : null}
              analyticsLinkType={'view-profile'}
            />
          ) : null}
        </div>
      }
    />
  );
};
export { DoctorCard as default, DoctorCard };
